/**
 * PII (Personally Identifiable Information) Handling Utilities
 *
 * SECURITY POLICY:
 * - email and name fields are considered PII and must be protected
 * - Never log, store, or transmit PII without proper authorization
 * - Only authorized admin users (BCT_ADMIN, BCT_PM) can access full User objects
 * - Use sanitized/anonymized versions for telemetry, error tracking, and logs
 * - Retention: PII in logs/localStorage should be purged after 30 days or immediately on logout
 */

import type { User } from '@/components/admin/types';
import type { User as AuthUser } from '@/stores/auth';

/**
 * Creates a safe, sanitized version of a User object with PII removed.
 * Use this for logging, error tracking, telemetry, or any non-admin context.
 *
 * @param user - The User object to sanitize
 * @returns User object with email and name replaced by safe identifiers
 */
export function sanitizeUser(user: User | AuthUser | null | undefined): {
  id: string;
  email: '[REDACTED]';
  name: '[REDACTED]';
  role: string;
  tenantId: string | null;
  isActive: boolean;
  [key: string]: unknown;
} | null {
  if (!user) return null;

  return {
    ...user,
    email: '[REDACTED]' as const,
    name: '[REDACTED]' as const,
  };
}

/**
 * Creates an anonymized hash of an email address for telemetry purposes.
 * Uses a simple hash function (not cryptographically secure, but sufficient for anonymization).
 *
 * @param email - The email address to anonymize
 * @returns A hashed identifier (e.g., "user_abc123")
 */
export function anonymizeEmail(email: string): string {
  // ─────────────────────────────────────────────────────────────────────────────
  // DJB2-variant hash algorithm for log anonymization
  // ─────────────────────────────────────────────────────────────────────────────
  // Formula: hash = ((hash << 5) - hash) + char  ≡  hash * 31 + char
  //
  // SECURITY NOTE: This is NOT cryptographically secure and is intentionally
  // simple. It's designed for log anonymization where we need:
  // - Consistent output for the same input (same user → same hash across logs)
  // - Fast computation (runs on every log entry)
  // - No security guarantees (this is for privacy, not security)
  //
  // The `hash & hash` forces JavaScript to treat the result as a 32-bit signed
  // integer, preventing floating-point precision issues with large numbers.
  // This is a JavaScript-specific idiom equivalent to `hash | 0`.
  // ─────────────────────────────────────────────────────────────────────────────
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    const char = email.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return `user_${Math.abs(hash).toString(36)}`;
}

/**
 * Creates an anonymized identifier from a name for telemetry purposes.
 *
 * @param name - The name to anonymize
 * @returns An anonymized identifier (e.g., "user_xyz789")
 */
export function anonymizeName(name: string): string {
  // Same DJB2-variant hash as anonymizeEmail - see that function for algorithm details
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return `user_${Math.abs(hash).toString(36)}`;
}

/**
 * Creates a telemetry-safe version of a User object with anonymized PII.
 * Use this when sending user data to analytics or telemetry services.
 *
 * @param user - The User object to anonymize
 * @returns User object with anonymized email and name
 */
export function anonymizeUserForTelemetry(user: User | AuthUser | null | undefined): {
  id: string;
  email: string;
  name: string;
  role: string;
  tenantId: string | null;
  isActive: boolean;
  [key: string]: unknown;
} | null {
  if (!user) return null;

  return {
    ...user,
    email: anonymizeEmail(user.email),
    name: anonymizeName(user.name),
  };
}

/**
 * Sanitizes an error object to remove any PII before logging.
 * Recursively searches for email/name fields and redacts them.
 *
 * @param error - Error object or any value that might contain PII
 * @returns Sanitized version safe for logging
 */
export function sanitizeError(error: unknown): unknown {
  if (error === null || error === undefined) return error;

  if (typeof error === 'string') {
    // Check if string contains email-like patterns and redact
    return error.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL_REDACTED]');
  }

  if (error instanceof Error) {
    return {
      name: error.name,
      message: sanitizeError(error.message) as string,
      stack: error.stack ? '[STACK_REDACTED]' : undefined,
    };
  }

  if (Array.isArray(error)) {
    return error.map(sanitizeError);
  }

  if (typeof error === 'object') {
    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(error)) {
      // Redact PII fields
      if (key.toLowerCase() === 'email' || key.toLowerCase() === 'name') {
        sanitized[key] = '[REDACTED]';
      } else if (
        key.toLowerCase().includes('user') &&
        typeof value === 'object' &&
        value !== null
      ) {
        // Recursively sanitize nested user objects
        sanitized[key] = sanitizeUser(value as User);
      } else {
        sanitized[key] = sanitizeError(value);
      }
    }
    return sanitized;
  }

  return error;
}

/**
 * Standardized error logging that always sanitizes PII before output.
 * Use this instead of `console.error('message', sanitizeError(err))`.
 *
 * @param message - Descriptive message about what operation failed
 * @param error - The error object (will be sanitized automatically)
 *
 * @example
 * ```ts
 * try {
 *   await api.post('/users', data)
 * } catch (err) {
 *   logError('Failed to create user', err)
 * }
 * ```
 */
export function logError(message: string, error?: unknown): void {
  if (error !== undefined) {
    console.error(message, sanitizeError(error));
  } else {
    console.error(message);
  }
}

/**
 * Checks if a user role is authorized to view user PII (email, name, etc.).
 * Only BCT staff roles (BCT_ADMIN, BCT_PM, BCT_CONSULTANT) can view full user details.
 *
 * @param role - The user's role (e.g., 'BCT_ADMIN', 'CUSTOMER_USER')
 * @returns true if the role can view user PII, false otherwise
 */
export function canViewUserPII(role: string | null | undefined): boolean {
  if (!role) return false;
  const bctRoles = ['BCT_ADMIN', 'BCT_PM', 'BCT_CONSULTANT'];
  return bctRoles.includes(role);
}
